import { __awaiter, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react';
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from '../../../badge/src';
import { Checkbox } from '../../../checkbox/src';
import { isRtl, LocaleContext, useLocalizeMessage } from '../../../i18n/src';
import { Icon } from '../../../icon/src';
import { SearchField } from '../../../search_field/src';
import { Skeleton } from '../../../skeleton/src';
import { Tooltip } from '../../../tooltip/src';
import { Text } from '../../../typography/src';
const PLACEMENT_MAP_LTR = {
    bottom: 'bottom-start',
    top: 'top-start',
};
const PLACEMENT_MAP_RTL = {
    bottom: 'bottom-end',
    top: 'top-end',
};
// Distance in pixels from bottom of scroll container to trigger loading more items
const SCROLL_LOAD_THRESHOLD = 50;
const DEFAULT_SEARCH_DEBOUNCE_MS = 250;
const SelectOpenEffects = ({ open, onOpen }) => {
    const prevOpen = useRef(open);
    useEffect(() => {
        if (!prevOpen.current && open) {
            onOpen();
        }
        prevOpen.current = open;
    }, [open, onOpen]);
    return null;
};
const classes = {
    size: {
        xs: 'nucleus-w-input-xs',
        sm: 'nucleus-w-input-sm',
        base: 'nucleus-w-input-base',
        lg: 'nucleus-w-input-lg',
        full: 'nucleus-w-input-full',
    },
    label: 'nucleus-font-medium nucleus-text-sm nucleus-text-text-primary nucleus-flex-none nucleus-order-none nucleus-grow-0 nucleus-block',
    required: 'nucleus-w-1.5 nucleus-h-5 nucleus-font-medium nucleus-text-sm nucleus-text-red-500 nucleus-flex-none nucleus-order-1 nucleus-grow-0 nucleus-pl-px',
    component: {
        common: 'nucleus-border-box nucleus-cursor-pointer nucleus-h-10 nucleus-relative nucleus-border-solid nucleus-border nucleus-w-full nucleus-cursor-default nucleus-rounded-md nucleus-py-2 sm:nucleus-text-sm nucleus-border focus-visible:nucleus-ring-1 focus:nucleus-outline-none focus:nucleus-ring-1 nucleus-text-start nucleus-pl-3 nucleus-pr-2 nucleus-flex nucleus-justify-between nucleus-items-center',
        commonColors: 'nucleus-border-neutral-300 nucleus-bg-white nucleus-text-text-primary focus-visible:nucleus-bg-neutral-50 focus-visible:nucleus-text-text-primary focus:nucleus-ring-primary-500 focus:nucleus-border-primary-500 focus-visible:nucleus-ring-primary-500 hover:nucleus-bg-neutral-50 hover:nucleus-text-text-primary hover:nucleus-border-neutral-500',
        disabled: '!nucleus-bg-neutral-200 nucleus-border-neutral-200 nucleus-text-text-disabled nucleus-pointer-events-none nucleus-border-0',
        error: 'nucleus-bg-white nucleus-border-red-500 nucleus-text-text-error hover:nucleus-border-red-500 focus:nucleus-ring-red-500 focus:nucleus-border-red-500 focus-visible:nucleus-ring-red-500',
    },
    description: 'nucleus-font-medium nucleus-text-xs nucleus-text-text-secondary nucleus-flex-none nucleus-grow-0 nucleus-mb-2 nucleus-mt-0 nucleus-p-0',
    error: 'nucleus-font-medium nucleus-text-xs nucleus-text-text-error nucleus-order-1 nucleus-grow nucleus-m-0',
    errorContainer: 'nucleus-mt-2 nucleus-flex nucleus-items-center nucleus-gap-2 nucleus-font-sans',
};
/**
 * Select allows a user to select an item from a prefilled list of items.
 * @remarks accessibility best practices for listbox: https://www.w3.org/WAI/ARIA/apg/patterns/listbox/
 */
export const Select = (_a) => {
    var { dataTestId, value, onChange, options, multiple, width = 'base', description, errorMessage, label, selectionLabel, hasError, required, hideRequiredIndicator, disabled, position = 'bottom', displayTooltip = false, displayTooltipOnlyOnOverflow = true, tooltipDelay = 500, placeholder, showMultipleSelectionBadges, searchableOptions, searchPlaceholder, searchableOptionsNoItemsText, onLoadMore, onSearchOptions, searchAsYouType = false, searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS } = _a, selectProps = __rest(_a, ["dataTestId", "value", "onChange", "options", "multiple", "width", "description", "errorMessage", "label", "selectionLabel", "hasError", "required", "hideRequiredIndicator", "disabled", "position", "displayTooltip", "displayTooltipOnlyOnOverflow", "tooltipDelay", "placeholder", "showMultipleSelectionBadges", "searchableOptions", "searchPlaceholder", "searchableOptionsNoItemsText", "onLoadMore", "onSearchOptions", "searchAsYouType", "searchDebounceMs"]);
    const translate = useLocalizeMessage();
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const searchInputRef = useRef(null);
    const searchDebounceTimeoutRef = useRef(null);
    const cancelPendingDebounce = useCallback(() => {
        if (searchDebounceTimeoutRef.current) {
            clearTimeout(searchDebounceTimeoutRef.current);
            searchDebounceTimeoutRef.current = null;
        }
    }, []);
    const { refs, floatingStyles, elements } = useFloating({
        placement: isRtl(useContext(LocaleContext)) ? PLACEMENT_MAP_RTL[position] : PLACEMENT_MAP_LTR[position],
        whileElementsMounted: autoUpdate,
        middleware: [offset(4), flip({ crossAxis: false })],
    });
    const filteredOptions = useMemo(() => {
        if (onSearchOptions) {
            return options;
        }
        if (!searchableOptions || !searchTerm.trim()) {
            return options;
        }
        return options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [options, searchTerm, searchableOptions, onSearchOptions]);
    const selectionLabelValue = useMemo(() => {
        if (!multiple) {
            return (value === null || value === void 0 ? void 0 : value.label) || placeholder;
        }
        if (selectionLabel) {
            return selectionLabel;
        }
        return value.length > 0 ? translate('Select an option ({ count })', { count: value.length }) : placeholder;
    }, [selectionLabel, value, multiple, placeholder]);
    const onCheckboxChange = useCallback((checked, selectedValue) => {
        if (multiple) {
            const newValues = checked ? [...value, selectedValue] : value.filter((v) => v.value !== selectedValue.value);
            onChange(newValues);
        }
    }, [value, multiple, onChange]);
    const removeBadge = useCallback((optionToRemove) => {
        if (multiple) {
            const newValues = value.filter((v) => v.value !== optionToRemove.value);
            onChange(newValues);
        }
    }, [value, multiple, onChange]);
    const handleSearchChange = useCallback((e) => {
        const nextSearchTerm = e.target.value;
        setSearchTerm(nextSearchTerm);
        if (onSearchOptions && searchAsYouType) {
            cancelPendingDebounce();
            searchDebounceTimeoutRef.current = setTimeout(() => {
                onSearchOptions(nextSearchTerm);
                searchDebounceTimeoutRef.current = null;
            }, searchDebounceMs);
        }
    }, [onSearchOptions, searchAsYouType, searchDebounceMs, cancelPendingDebounce]);
    const handleSearchClear = useCallback(() => {
        cancelPendingDebounce();
        setSearchTerm('');
        onSearchOptions === null || onSearchOptions === void 0 ? void 0 : onSearchOptions('');
    }, [onSearchOptions, cancelPendingDebounce]);
    const handleSearchSubmit = useCallback((term) => {
        cancelPendingDebounce();
        onSearchOptions === null || onSearchOptions === void 0 ? void 0 : onSearchOptions(term);
    }, [onSearchOptions, cancelPendingDebounce]);
    const handleSearchKeyDown = useCallback((e) => {
        const target = e.target;
        if (target.id === 'search') {
            if (e.key === 'Enter' && onSearchOptions) {
                handleSearchSubmit(target.value);
            }
            e.stopPropagation();
        }
    }, [onSearchOptions, handleSearchSubmit]);
    const handleOpen = useCallback(() => {
        var _a;
        cancelPendingDebounce();
        setSearchTerm('');
        onSearchOptions === null || onSearchOptions === void 0 ? void 0 : onSearchOptions('');
        if (searchableOptions) {
            (_a = searchInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [onSearchOptions, searchableOptions, cancelPendingDebounce]);
    useEffect(() => {
        if (elements.floating) {
            elements.floating.scrollTop = 1; // Trigger the scrollbar to appear
            elements.floating.scrollTop = 0; // Reset to original position
        }
    }, [elements]);
    /**
     * Handles the scroll event for the associated element, and determine whether
     * a "load more" action should be triggered
     */
    const handleScroll = useCallback((event) => __awaiter(void 0, void 0, void 0, function* () {
        if (!onLoadMore || isLoadingMore) {
            return;
        }
        const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
        handleLoadMore(scrollHeight - scrollTop, clientHeight);
    }), [onLoadMore, isLoadingMore]);
    /**
     * Identifies cases where the container has no scroll but new pages are available
     */
    useEffect(() => {
        if (!onLoadMore || isLoadingMore || !elements.floating) {
            return;
        }
        const rafId = requestAnimationFrame(() => {
            const container = elements.floating;
            if (!onLoadMore || !container || container.clientHeight === 0) {
                return;
            }
            handleLoadMore(container.scrollHeight, container.clientHeight);
        });
        return () => cancelAnimationFrame(rafId);
    }, [filteredOptions, onLoadMore, isLoadingMore, elements.floating]);
    useEffect(() => {
        return cancelPendingDebounce;
    }, [cancelPendingDebounce]);
    const handleLoadMore = (scrollHeight, clientHeight) => __awaiter(void 0, void 0, void 0, function* () {
        if (onLoadMore && scrollHeight <= clientHeight + SCROLL_LOAD_THRESHOLD) {
            setIsLoadingMore(true);
            yield onLoadMore();
            setIsLoadingMore(false);
        }
    });
    return (_jsxs("div", { className: `${classes.size[width]} nucleus-inline-block`, children: [_jsx(Listbox, Object.assign({ multiple: multiple, value: value, onChange: onChange, by: 'value', as: 'div', className: 'nucleus-relative nucleus-font-sans', disabled: disabled, "aria-label": label, "aria-required": required, "data-testid": dataTestId }, selectProps, { children: ({ open }) => {
                    var _a;
                    return (_jsxs(_Fragment, { children: [_jsx(SelectOpenEffects, { open: open, onOpen: handleOpen }), label && (_jsxs(Label, { className: classNames(classes.label, classes.size[width], !description && 'nucleus-mb-2'), children: [label, required && !hideRequiredIndicator && _jsx("span", { className: classes.required, children: "*" })] })), description && _jsx("p", { className: classNames(classes.description, classes.size[width]), children: description }), _jsxs(ListboxButton, { className: classNames(classes.component.common, {
                                    [classes.component.disabled]: disabled,
                                    [classes.component.error]: hasError,
                                    [classes.component.commonColors]: !disabled && !hasError && !open,
                                    'nucleus-bg-primary-75 nucleus-text-text-primary nucleus-border-0': open,
                                }), ref: refs.setReference, "aria-disabled": !!disabled, children: [displayTooltip ? (_jsx(Tooltip, { title: selectionLabelValue, wrapperClassName: 'nucleus-truncate nucleus-text-sm nucleus-inline', as: 'span', delayShow: tooltipDelay, displayOnlyOnOverflow: displayTooltipOnlyOnOverflow, children: selectionLabelValue })) : (_jsx("span", { className: 'nucleus-truncate', children: selectionLabelValue })), _jsxs("div", { className: 'nucleus-ml-auto', children: [open && _jsx(Icon, { name: 'chevronUp', color: 'grayDark', size: 'sm' }), !open && _jsx(Icon, { name: 'chevronDown', color: 'gray', size: 'sm' })] })] }), createPortal(_jsxs(ListboxOptions, { className: 'nucleus-absolute nucleus-z-50 nucleus-max-h-60 nucleus-list-none nucleus-overflow-auto nucleus-bg-white nucleus-font-sans nucleus-text-base nucleus-shadow-xl nucleus-m-0 nucleus-mt-1 nucleus-rounded-md nucleus-p-0 nucleus-pb-1 focus:nucleus-outline-none sm:nucleus-text-sm', ref: refs.setFloating, style: Object.assign(Object.assign({}, floatingStyles), { width: (_a = elements.reference) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width }), onScroll: handleScroll, onKeyDownCapture: handleSearchKeyDown, children: [searchableOptions && (_jsx("div", { className: 'nucleus-p-2', children: _jsx(SearchField, { ref: searchInputRef, ariaLabel: translate('Search options'), placeholder: searchPlaceholder || translate('Search options...'), value: searchTerm, onChange: handleSearchChange, onClear: handleSearchClear, dataTestId: `${dataTestId}-search-options`, width: 'full' }) })), filteredOptions.map((option) => {
                                        if (option.hidden) {
                                            return null;
                                        }
                                        return (_jsx(ListboxOption, { value: option, "data-label": option.label, "data-value": option.value, disabled: option.isDisabled, className: ({ selected }) => classNames('nucleus-py-2 nucleus-pl-3 nucleus-pr-10 nucleus-cursor-pointer', {
                                                'nucleus-text-text-disabled': option.isDisabled,
                                                'nucleus-pointer-events-none': option.isDisabled && !option.disabledTooltip,
                                                'nucleus-cursor-default': option.isDisabled && option.disabledTooltip,
                                                'nucleus-bg-primary-500 nucleus-text-white': !option.isDisabled && !multiple && selected,
                                                'ui-active:nucleus-bg-neutral-200 ui-active:nucleus-text-black ui-not-active:nucleus-bg-white ui-not-active:nucleus-text-black': !option.isDisabled && (!selected || multiple),
                                            }), children: ({ selected }) => {
                                                const content = multiple ? (_jsxs("div", { className: 'nucleus-flex nucleus-items-center nucleus-gap-3', children: [_jsx(Checkbox, { checked: selected, onChange: (checked) => onCheckboxChange(checked, option), label: `Checkbox select option ${option.label}`, hideLabel: true, disabled: option.isDisabled }), _jsx(Text, { size: 'sm', children: option.label })] })) : (_jsx(Text, { size: 'sm', children: option.label }));
                                                return option.isDisabled && option.disabledTooltip ? (_jsx(Tooltip, { title: option.disabledTooltip, children: _jsx("div", { className: `${classes.size[width]} nucleus-text-start`, children: content }) })) : (content);
                                            } }, option.value));
                                    }), filteredOptions.length === 0 && (_jsx("div", { className: 'nucleus-py-2 nucleus-px-3 nucleus-pointer-events-none', children: _jsx(Text, { size: 'sm', className: 'nucleus-text-text-secondary', children: searchableOptionsNoItemsText || translate('No options found') }) })), onLoadMore &&
                                        isLoadingMore &&
                                        Array.from({ length: 2 }).map((_, i) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        _jsx("div", { className: 'nucleus-py-2 nucleus-pl-3 nucleus-pr-10', children: _jsx(Skeleton, { height: 16, width: `${i === 0 ? 90 : 75}%` }) }, `skeleton-${i}`)))] }), document.body)] }));
                } })), hasError && errorMessage && (_jsxs("div", { className: classNames(classes.size[width], classes.errorContainer), children: [_jsx("span", { children: _jsx(Icon, { name: 'error', color: 'red', size: 'sm' }) }), _jsx("p", { className: classes.error, role: 'alert', children: errorMessage })] })), multiple && showMultipleSelectionBadges && value && value.length > 0 && (_jsx("div", { className: classNames(classes.size[width], 'nucleus-mt-2 nucleus-flex nucleus-flex-wrap nucleus-gap-2'), children: value.map((selectedOption) => (_jsx(Badge, { variant: 'common', label: selectedOption.label, endIcon: {
                        name: 'close',
                        size: 'sm',
                        onClick: () => {
                            removeBadge(selectedOption);
                        },
                    } }, selectedOption.value))) }))] }));
};
//# sourceMappingURL=select.component.js.map